/**
 * useKeyboardShortcuts — hook reutilizável para atalhos de teclado em telas
 * de produtividade (M5.E5.S2.2 / TH-53360).
 *
 * Comportamento:
 * - Listeners globais `keydown` enquanto o componente está montado.
 * - **Desabilita** atalhos quando o foco está em `<input>`, `<textarea>`,
 *   `<select>` ou `contentEditable=true` — usuário digitando não pode
 *   disparar ação acidental.
 * - Ignora atalhos quando há modifier (ctrl/cmd/alt/meta) — não conflita
 *   com atalhos do navegador.
 * - Lookup case-insensitive (`A` e `a` são iguais).
 * - Suporta também teclas de seta (`ArrowRight`, `ArrowLeft`, etc.) e
 *   `?` para mostrar painel de ajuda.
 *
 * Uso:
 * ```ts
 * useKeyboardShortcuts({
 *   a: () => approve(),
 *   r: () => refazer(),
 *   x: () => markNA(),
 *   ArrowRight: () => proximo(),
 *   "?": () => setShowHelp(true),
 * }, { enabled: !showHelp });
 * ```
 */

import { useEffect } from "react";

export type ShortcutHandlers = Record<string, () => void>;

export interface UseShortcutsOptions {
  /** Quando `false`, listeners não rodam (útil ao abrir um modal). */
  enabled?: boolean;
}

function isEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  // `isContentEditable` é flag computada — em jsdom é frequentemente
  // false mesmo com `contenteditable="true"`. Fallback no atributo cru.
  if (target.isContentEditable) return true;
  const attr = target.getAttribute("contenteditable");
  if (attr === "" || attr?.toLowerCase() === "true") return true;
  return false;
}

export function useKeyboardShortcuts(
  handlers: ShortcutHandlers,
  options: UseShortcutsOptions = {},
): void {
  const { enabled = true } = options;

  useEffect(() => {
    if (!enabled) return;

    const handler = (e: KeyboardEvent) => {
      // Ignora atalhos com modifier — não conflita com browser shortcuts.
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (isEditable(e.target)) return;

      // Lookup: tenta o key cru (preserva ArrowRight, "?") e o lower-case
      // (preserva a/A indistintos).
      const fn =
        handlers[e.key] ?? handlers[e.key.toLowerCase()];
      if (fn) {
        e.preventDefault();
        fn();
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handlers, enabled]);
}
